import { NewsItem as NewsItemType } from '@/lib/types';
import { cn, formatDate, getRegionColor, getSentimentColor } from '@/lib/utils';
import { RefreshCw } from 'lucide-react';
import React from 'react';

interface NewsItemProps {
  item: NewsItemType;
  index: number;
  isSelected: boolean;
  onClick: (item: NewsItemType) => void;
}

const NewsItem: React.FC<NewsItemProps> = ({ item, index, isSelected, onClick }) => {
  const handleClick = (e: React.MouseEvent) => {
    // Check if Command key (Mac) or Ctrl key (Windows/Linux) is pressed
    if (e.metaKey || e.ctrlKey) {
      // Open the news source link in a new tab
      if (item.link) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    console.log("NewsItem clicked:", item.id, item.title);
    onClick(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Enter and Space key presses
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Check if Command key (Mac) or Ctrl key (Windows/Linux) is pressed
      if (e.metaKey || e.ctrlKey) {
        // Open the news source link in a new tab
        if (item.link) {
          window.open(item.link, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      onClick(item);
    }
  };

  // Determine if the item has been updated
  const isUpdated = item.wasUpdated && item.updatedAt;

  return (
    <article
      className={cn(
        "news-item cursor-pointer",
        isSelected && "bg-[#1a1a1a]",
        isSelected && "border-l-2 border-l-terminal"
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label={`${item.title} — ${item.provider}`}
    >
      <div className="news-id text-terminal">
        {index})
      </div>
      <div className={cn("news-title", getSentimentColor(item.sentiment))}>
        <h3 className="inline">{item.title}</h3>
      </div>
      <div className="news-metadata">
        <div className="news-provider">{item.provider}</div>
        {item.region && (
          <div className={cn("news-region", getRegionColor(item.region))}>
            {item.region}
          </div>
        )}
        <div className="news-time">
          {isUpdated ? (
            <time dateTime={item.updatedAt || ''} title={`Originally published: ${formatDate(item.originalPubDate || item.pubDate)}`}>
              <span className="mr-1 text-green-400">
                <RefreshCw size={12} className="inline-block" aria-hidden="true" />
              </span>
              Updated: {item.updatedAt ? formatDate(item.updatedAt) : ''}
            </time>
          ) : (
            <time dateTime={item.pubDate}>{formatDate(item.pubDate)}</time>
          )}
        </div>
      </div>
    </article>
  );
};

export default React.memo(NewsItem);
